import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Heart, LogOut, Plus, Pencil, Trash2, X, ChevronLeft, Mail, Phone } from "lucide-react";

const empty = { first_name: "", last_name: "", email: "", phone: "" };

export default function Contacts() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [contacts, setContacts] = useState([]);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = () => api.get("/contacts").then(({ data }) => setContacts(data)).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);
  const handleLogout = () => { logout(); navigate("/login"); };

  const save = async (form) => {
    try {
      if (form.id) await api.put(`/contacts/${form.id}`, form);
      else await api.post("/contacts", form);
      toast.success("Contact saved"); setModal(null); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => {
    try { await api.delete(`/contacts/${id}`); toast.success("Contact removed"); load(); }
    catch { toast.error("Could not remove contact"); }
  };

  return (
    <div className="min-h-screen" style={{ background: "#F5F4F0" }} data-testid="contacts-page">
      <header className="border-b border-[#E8E6E1] bg-white/70 backdrop-blur-xl sticky top-0 z-30">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 h-16 flex items-center justify-between">
          <Link to="/" data-testid="brand-home" className="flex items-center gap-2"><Heart size={18} className="text-[#8C9A86]" fill="#8C9A86" /><span className="font-serif text-2xl tracking-tight">Evermore</span></Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-stone-500 hidden sm:inline">{user?.name}</span>
            <Link to="/app" data-testid="back-to-events" className="text-sm text-stone-500 hover:text-[#8C9A86]">My events</Link>
            <Link to="/account" data-testid="account-link" className="text-sm text-stone-500 hover:text-[#8C9A86]">Account</Link>
            <button onClick={handleLogout} data-testid="logout-btn" className="flex items-center gap-1.5 text-sm text-stone-500 hover:text-[#C67A7A]"><LogOut size={15} /> Sign out</button>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-5 sm:px-8 py-10 lg:py-14">
        <Link to="/app" className="flex items-center gap-1.5 text-xs text-stone-400 hover:text-stone-700 mb-4"><ChevronLeft size={14} /> Back to my events</Link>
        <div className="flex items-end justify-between flex-wrap gap-4 mb-8">
          <div>
            <p className="overline">Address book</p>
            <h1 className="font-serif text-4xl sm:text-5xl tracking-tight">Favourite contacts</h1>
            <p className="text-stone-500 mt-3 max-w-xl">Save the people you invite often. Add them to any event's guest list in one tap.</p>
          </div>
          <button onClick={() => setModal({ ...empty })} data-testid="add-contact-btn" className="flex items-center gap-2 rounded-full bg-[#2C2A28] text-white px-6 py-3 text-sm hover:bg-[#8C9A86] transition-colors"><Plus size={16} /> Add contact</button>
        </div>

        {loading ? (
          <p className="text-stone-400">Loading…</p>
        ) : contacts.length === 0 ? (
          <div className="bg-white border border-dashed border-[#E8E6E1] rounded-xl p-12 text-center text-sm text-stone-400" data-testid="contacts-empty">No contacts yet. Add someone you invite often, or tick "Save to my favourite contacts" when adding a guest.</div>
        ) : (
          <div className="bg-white border border-[#E8E6E1] rounded-lg overflow-hidden" data-testid="contacts-list">
            {contacts.map((c) => (
              <div key={c.id} data-testid={`contact-row-${c.id}`} className="flex items-center gap-4 px-5 py-4 border-b border-[#F0EEE9] last:border-0 hover:bg-[#FDFBF7] transition-colors">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-stone-800 truncate">{c.first_name} {c.last_name}</p>
                  <p className="text-xs text-stone-400 truncate flex flex-wrap gap-x-3">
                    {c.email && <span className="flex items-center gap-1"><Mail size={12} /> {c.email}</span>}
                    {c.phone && <span className="flex items-center gap-1"><Phone size={12} /> {c.phone}</span>}
                    {!c.email && !c.phone && <span>No contact details</span>}
                  </p>
                </div>
                <button onClick={() => setModal({ ...c })} data-testid={`edit-contact-${c.id}`} className="p-2 text-stone-400 hover:text-stone-700 transition-colors"><Pencil size={15} /></button>
                <button onClick={() => remove(c.id)} data-testid={`delete-contact-${c.id}`} className="p-2 text-stone-400 hover:text-[#C67A7A] transition-colors"><Trash2 size={15} /></button>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && <ContactModal initial={modal} onClose={() => setModal(null)} onSave={save} />}
    </div>
  );
}

function ContactModal({ initial, onClose, onSave }) {
  const [form, setForm] = useState(initial);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = () => { if (!form.first_name?.trim()) { toast.error("Please enter a first name."); return; } onSave(form); };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/30 backdrop-blur-sm" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-md p-6 fade-up" onClick={(e) => e.stopPropagation()} data-testid="contact-modal">
        <div className="flex items-center justify-between mb-5">
          <h3 className="font-serif text-2xl">{form.id ? "Edit contact" : "Add contact"}</h3>
          <button onClick={onClose} className="text-stone-400 hover:text-stone-700"><X size={20} /></button>
        </div>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <CF label="First name" value={form.first_name} onChange={(v) => set("first_name", v)} testid="cm-first" />
            <CF label="Last name" value={form.last_name} onChange={(v) => set("last_name", v)} testid="cm-last" />
          </div>
          <CF label="Email" value={form.email} onChange={(v) => set("email", v)} testid="cm-email" />
          <CF label="Phone" value={form.phone} onChange={(v) => set("phone", v)} testid="cm-phone" />
        </div>
        <div className="flex gap-3 mt-6">
          <button onClick={onClose} className="flex-1 rounded-full border border-[#E8E6E1] py-2.5 text-sm hover:bg-stone-50">Cancel</button>
          <button onClick={submit} data-testid="cm-save" className="flex-1 rounded-full bg-[#2C2A28] text-white py-2.5 text-sm hover:bg-[#8C9A86] transition-colors">Save</button>
        </div>
      </div>
    </div>
  );
}

function CF({ label, value, onChange, testid }) {
  return (
    <label className="block">
      <span className="text-xs font-medium text-stone-500 mb-1.5 block">{label}</span>
      <input value={value || ""} onChange={(e) => onChange(e.target.value)} data-testid={testid}
        className="w-full rounded-md border border-[#E8E6E1] bg-white px-3.5 py-2.5 text-sm outline-none focus:border-[#8C9A86] focus:ring-2 focus:ring-[#8C9A86]/20" />
    </label>
  );
}
